import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../api';

const CONTENT_TYPES = ['challenge', 'journey', 'seasonal', 'tip', 'announcement', 'guide'];

export default function ContentPage() {
  const [items, setItems] = useState([]);
  const [, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ contentType: 'challenge', title: '', bodyText: '', displayOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [showAIDraft, setShowAIDraft] = useState(false);
  const [draftType, setDraftType] = useState('tip');
  const [generating, setGenerating] = useState(false);
  const [draftError, setDraftError] = useState('');

  const fetchContent = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: '50', offset: String(offset) };
      if (typeFilter) params.type = typeFilter;
      const data = await api.getContent(params);
      setItems(data.content);
      setPagination(data.pagination);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { fetchContent(0); }, [fetchContent]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createContent(form);
      setShowForm(false);
      setForm({ contentType: 'challenge', title: '', bodyText: '', displayOrder: 0 });
      fetchContent(0);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleToggle = async (item) => {
    try {
      await api.updateContent(item.id, { isActive: !item.isActive });
      setItems((prev) => prev.map((c) => c.id === item.id ? { ...c, isActive: !c.isActive } : c));
    } catch { /* ignore */ }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.deleteContent(item.id);
      setItems((prev) => prev.filter((c) => c.id !== item.id));
    } catch { /* ignore */ }
  };

  const handleGenerateDraft = async () => {
    setGenerating(true);
    setDraftError('');
    try {
      const data = await api.generateAIDraft({ contentType: draftType });
      setForm({
        contentType: data.draft.contentType,
        title: data.draft.title,
        bodyText: data.draft.body,
        displayOrder: 0,
      });
      setShowAIDraft(false);
      setShowForm(true);
    } catch (err) {
      setDraftError(err.message || 'Failed to generate draft.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Content Management</h1>
        <p>Manage challenges, journeys, seasonal items, and guides</p>
      </div>

      <div className="toolbar">
        <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => { setShowAIDraft(!showAIDraft); if (showForm) setShowForm(false); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {showAIDraft ? 'Cancel' : 'AI Draft'}
          </button>
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showAIDraft) setShowAIDraft(false); }}>
            {showForm ? 'Cancel' : '+ New content'}
          </button>
        </div>
      </div>

      {showAIDraft && (
        <div className="card" style={{ marginBottom: 20, padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Generate AI Draft</h3>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#666' }}>
            Select a content type and let AI generate a draft for you to review, edit, and publish.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label>Content type</label>
              <select className="filter-select" style={{ width: '100%' }} value={draftType} onChange={(e) => setDraftType(e.target.value)}>
                {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleGenerateDraft} disabled={generating} style={{ whiteSpace: 'nowrap' }}>
              {generating ? 'Generating...' : 'Generate Draft'}
            </button>
          </div>
          {draftError && (
            <p style={{ color: '#f44336', fontSize: 13, marginTop: 12, marginBottom: 0 }}>{draftError}</p>
          )}
        </div>
      )}

      {showForm && (
        <form className="card" style={{ marginBottom: 20 }} onSubmit={handleCreate}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Type</label>
              <select className="filter-select" style={{ width: '100%' }} value={form.contentType} onChange={(e) => setForm({ ...form, contentType: e.target.value })}>
                {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Display Order</label>
              <input type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="form-group">
            <label>Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Body</label>
            <textarea style={{ width: '100%', minHeight: 100, padding: 10, border: '1px solid #e0e0e0', borderRadius: 8, fontFamily: 'inherit', fontSize: 14 }} value={form.bodyText} onChange={(e) => setForm({ ...form, bodyText: e.target.value })} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading content...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No content items found.</p></div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead><tr><th>Order</th><th>Type</th><th>Title</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.displayOrder}</td>
                    <td><span className="badge badge-blue">{item.contentType}</span></td>
                    <td style={{ fontWeight: 500 }}>{item.title}</td>
                    <td><span className={`badge ${item.isActive ? 'badge-green' : 'badge-red'}`}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ fontSize: 13, color: '#888' }}>{new Date(item.createdAt).toLocaleDateString('en-ZA')}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className={`btn btn-sm ${item.isActive ? 'btn-secondary' : 'btn-primary'}`} onClick={() => handleToggle(item)} style={{ marginRight: 6 }}>
                        {item.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
